import React, { useEffect, useState } from "react";
import BsModal from "../Common/BsModal";
import { useAccounts } from "../../DataStore/AccountsContext";
import ajaxAccountChart from "../../util/remote/ajaxAccountChart";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../Common/Notification";

/**
 * ManageBankModal
 * - Shows bank name
 * - Shows currently linked ledger account (if any)
 * - Allows unlink / link (one-to-one)
 * - When linking, user selects from Asset accounts only (dummy data)
 */
const ManageBankModal = ({
  show,
  onClose,
  bank = null,
  onSave = () => {},
  buttonLabel = "Save Changes",
}) => {

  const {getBankAccounts, bankAccounts} = useAccounts();
  useEffect(()=>{
    getBankAccounts(true);
  }, []);
  const {setNotify} = useTheme();
  
 
  // -----------------------------
  // Local state
  // -----------------------------
  const [linkedLedgerId, setLinkedLedgerId] = useState(
    bank?.account_id ?? null
  );
  const [selecting, setSelecting] = useState(false);
  const [pendingLedgerId, setPendingLedgerId] = useState(
    bank?.account_id ?? ""
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");


  const dirty =
    ( bank?.account_id?? null) !== (linkedLedgerId ?? null);

  const handleUnlink = () => {
    setError("");
    setLinkedLedgerId(null);
    setPendingLedgerId("");
    setSelecting(false);
  };

  const openLinkPicker = () => {
    setError("");
    setSelecting(true);
    setPendingLedgerId(linkedLedgerId ?? "");
  };

  const cancelPicker = () => {
    setError("");
    setSelecting(false);
    setPendingLedgerId(linkedLedgerId ?? "");
  };

  const applyLink = () => {
    setError("");
    const nextId = pendingLedgerId ? Number(pendingLedgerId) : null;

    if (!nextId) {
      setError("Please select an asset ledger account to link.");
      return;
    }

    setLinkedLedgerId(nextId);
    setSelecting(false);
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    const payload={
        bank_id: bank?.bank_id,
        account_id: linkedLedgerId, // null means unlinked
      };

    const serverResponse = linkedLedgerId ? await ajaxAccountChart.linkBank(payload) : await ajaxAccountChart.unlinkBank(payload);
    setSaving(false);
    if(serverResponse.status === "OK"){
       setNotify(<Notification type='success' message={serverResponse.message} />)
       await getBankAccounts(true);
       onSave();
       onClose();
    }else{
      setError(serverResponse.message);
    }
  };

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="Manage Bank"
      size="modal-md"
      footer={
        <>
          <button
            type="button"
            className="btn btn-light"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-success"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-save mr-1" /> {buttonLabel}
              </>
            )}
          </button>
        </>
      }
    >
      {/* Header */}
      <div
        className="p-3 mb-3"
        style={{
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="mr-3 d-flex align-items-center justify-content-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "rgba(0,123,255,0.12)",
                border: "1px solid rgba(0,123,255,0.25)",
              }}
            >
              <i className="fa fa-university text-primary" />
            </div>

            <div>
              <div className="font-weight-bold" style={{ fontSize: 16 }}>
                {bank?.bank_name}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {bank?.account_number} • 
              </div>
            </div>
          </div>

          <span
            className={`badge ${
              bank?.account_id*1 > 1 ? "badge-success" : "badge-secondary"
            }`}
            style={{ padding: "7px 10px", borderRadius: 999 }}
          >
            {bank?.account_id*1 > 1 ? "Linked" : "Not linked"}
          </span>
        </div>
      </div>

      {/* Current link */}
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="font-weight-bold">
            <i className="fa fa-link text-info mr-2" />
            Linked Ledger Account
          </div>

          {bank?.account_id*1 > 1 ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleUnlink}
              disabled={saving}
              title="Unlink this bank from its ledger account"
            >
              <i className="fa fa-unlink mr-1" />
              Unlink
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={openLinkPicker}
              disabled={saving}
              title="Link this bank to an asset ledger account"
            >
              <i className="fa fa-plus mr-1" />
              Link Account
            </button>
          )}
        </div>

        <div
          className="p-3"
          style={{
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          {bank?.account_info ? (
            <div className="d-flex align-items-start justify-content-between">
              <div>
                <div className="font-weight-bold">
                  {bank?.account_info.account_number} — {bank?.account_info.account_name}
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Category: {bank?.account_info.account_category.category_name}
                </div>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={openLinkPicker}
                disabled={saving}
                title="Change linked account"
              >
                <i className="fa fa-exchange-alt mr-1" />
                Change
              </button>
            </div>
          ) : (
            <div className="text-muted" style={{ fontSize: 13 }}>
              No ledger account linked to this bank yet.
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={openLinkPicker}
                  disabled={saving}
                >
                  <i className="fa fa-link mr-1" />
                  Link an Asset Account
                </button>
              </div>
            </div>
          )}
        </div>

        {error ? (
          <div className="alert alert-danger mt-3 mb-0 py-2">
            <i className="fa fa-exclamation-triangle mr-2" />
            {error}
          </div>
        ) : null}
      </div>

      {/* Link picker */}
      {selecting && (
        <div
          className="p-3"
          style={{
            borderRadius: 12,
            border: "1px solid rgba(0,123,255,0.25)",
            background: "rgba(0,123,255,0.06)",
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="font-weight-bold">
              <i className="fa fa-wallet text-primary mr-2" />
              Select an Asset Ledger Account
            </div>

            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={cancelPicker}
              disabled={saving}
            >
              Close
            </button>
          </div>

          <div className="form-group mb-2">
            <label className="mb-1 text-muted" style={{ fontSize: 13 }}>
              Asset Accounts
            </label>

            <select
              className="form-control"
              value={pendingLedgerId}
              onChange={(e) => setPendingLedgerId(e.target.value)}
              disabled={saving}
            >
              <option value="">— Select account —</option>
              {Array.isArray(bankAccounts) && bankAccounts.map((a) => {
                const taken = a.bank_id * 1 > 0;
                return (
                  <option key={a.account_id} value={a.account_id} disabled={taken}>
                    {a.account_number} — {a.account_name}
                    {taken ? " (already linked)" : ""}
                  </option>
                );
              })}
            </select>

            <small className="text-muted">
              Each bank can link to only one ledger account, and each ledger
              account can link to only one bank.
            </small>
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary mr-2"
              onClick={cancelPicker}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={applyLink}
              disabled={saving}
            >
              <i className="fa fa-check mr-1" />
              Apply Link
            </button>
          </div>
        </div>
      )}

      {/* tiny note */}
      <div className="mt-3 text-muted" style={{ fontSize: 12 }}>
        Tip: link your bank to an Asset ledger account (e.g. “Stanbic Bank - UGX”)
        to enable clean reconciliation and reporting.
      </div>
    </BsModal>
  );
};

export default ManageBankModal;
